import React, { forwardRef, ReactNode } from 'react'
import { Text, Flex } from 'components/elements'
import {
  default as MuiRating,
  RatingProps as MuiRatingProps,
} from '@mui/material/Rating'
import { CSS, styled } from 'theme'
import { FieldError } from 'react-hook-form'
import {
  EmptyStarIcon,
  FillStarIcon,
  HalfEmptyStarIcon,
} from 'components/icons'

interface RatingProps extends MuiRatingProps {
  css?: CSS
  titleCss?: CSS
  label?: string
  onChange: (event: React.SyntheticEvent, value: number | null) => void
  error?: FieldError | string
  required?: boolean
  disabled?: boolean
}

export const Rating = forwardRef<HTMLDivElement, RatingProps>(
  (
    { css, titleCss, label, error, required, color, ...rest }: RatingProps,
    ref
  ) => {
    return (
      <RatingWrap direction={'column'} align={'start'} css={{ ...css }}>
        {label && (
          <Text
            as="label"
            css={{ width: '100%', mb: '$space$10', ...titleCss }}
          >
            {label}
            {required ? (
              <Text css={{ color: '$error', ml: '$space$6' }}>*</Text>
            ) : null}
          </Text>
        )}
        <MuiRating
          ref={ref}
          {...rest}
          precision={1}
          color={error ? 'error' : color}
          emptyIcon={<EmptyStarIcon />}
          icon={<FillStarIcon />}
          IconContainerComponent={props => {
            const { value, ...other } = props
            if (value % 1 === 0.5) {
              return (
                <HalfEmptyStarIcon
                  {...(other as React.SVGProps<SVGSVGElement>)}
                />
              )
            }
            return <span {...other} />
          }}
        />
        {!!error && (
          <Text
            color="error"
            className="errorField"
            css={{ textAlign: 'start', width: '100%' }}
          >
            {error as ReactNode}
          </Text>
        )}
      </RatingWrap>
    )
  }
)

export const RatingWrap = styled(Flex, {
  marginBottom: '$space$20',
  variants: {
    size: {
      // sm: {
      //   p: '$space$2 $space$4',
      //   borderRadius: '$radii$xs',
      //   maxHeight: 26,
      // },
    },
    color: {
      primary: {
        '.MuiRating-iconEmpty': {
          svg: {
            fill: '$gray300',
          },
        },
        '.MuiRating-iconFilled': {
          fill: '$primaryBase',
        },
      },
      secondary: {
        '.MuiRating-iconEmpty': {
          svg: {
            fill: '$gray300',
          },
        },
        '.MuiRating-iconFilled': {
          fill: '$secondaryBase',
        },
      },
      gray: {
        '.MuiRating-iconEmpty': {
          svg: {
            fill: '$gray300',
          },
        },
        '.MuiRating-iconFilled': {
          fill: '$gray500',
        },
      },
      warning: {
        '.MuiRating-iconEmpty': {
          svg: {
            fill: '$gray300',
          },
        },
        '.MuiRating-iconFilled': {
          fill: '$warning500',
        },
      },
      error: {
        '.MuiRating-iconEmpty': {
          svg: {
            fill: '$gray300',
          },
        },
        '.MuiRating-iconFilled': {
          fill: '$error500',
        },
      },
      success: {
        '.MuiRating-iconEmpty': {
          svg: {
            fill: '$gray300',
          },
        },
        '.MuiRating-iconFilled': {
          fill: '$success500',
        },
      },
    },
  },
  defaultVariants: {
    size: 'sm',
    color: 'primary',
  },
})
